// Live Documents page. GET /api/v1/documents[?client_id=].
// Download button is disabled until the PDF-gen slice (bytes streamed
// from a Postgres bytea sidecar via an authenticated endpoint).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { KickerRow, PageHeader, Pill } from '../../components/shell';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

interface Doc {
  id: string;
  kind: string;
  kind_label: string;
  title: string;
  uploaded_at: string;
  visible_to_client: boolean;
  has_file: boolean;
}

interface DocumentsPayload {
  client_id: string;
  documents: Doc[];
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

export default function Documents() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const clientId = params.get('client_id');
  const [data, setData] = useState<DocumentsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const docUrl = (id: string) => `/api/v1/documents/${id}` + (clientId ? `?client_id=${clientId}` : '');

  useEffect(() => {
    let cancelled = false;
    if (user && user.role !== 'client' && !clientId) {
      navigate('/fund', { replace: true });
      return;
    }
    const url = '/documents/' + (clientId ? `?client_id=${clientId}` : '');
    api<DocumentsPayload>(url)
      .then(d => { if (!cancelled) setData(d); })
      .catch((e: any) => {
        if (cancelled) return;
        if (e?.status === 404 && user?.role !== 'client') { navigate('/fund', { replace: true }); return; }
        setError(e?.status === 409 ? 'Your portfolio is being set up. Transactions and reports will appear here once trading begins.' : 'Failed to load documents.');
      });
    return () => { cancelled = true; };
  }, [clientId, user, navigate, reloadKey]);

  const grouped = useMemo(() => {
    const map = new Map<string, Doc[]>();
    (data?.documents ?? []).forEach(d => {
      const arr = map.get(d.kind_label) ?? [];
      arr.push(d);
      map.set(d.kind_label, arr);
    });
    return [...map.entries()];
  }, [data]);

  if (error) return <div className="view container"><p className="serif-body" style={{ color: 'var(--accent)' }}>{error}</p></div>;
  if (!data) return (
    <div className="view container">
      <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
                  textTransform: 'uppercase', color: 'var(--ink-3)' }}>Loading…</p>
    </div>
  );

  return (
    <div className="view container">
      <PageHeader
        kicker="DOCUMENTS"
        title={<><em>Records</em> and agreements.</>}
        meta={<span className="label-s">SECURELY HELD · AUDIT-LOGGED</span>}
      />

      {user?.role === 'manager' && clientId && (
        <UploadRow clientId={clientId} onUploaded={() => setReloadKey(k => k + 1)} />
      )}
      {/* Phase 9.b — investor self-service upload (beneficial-owner forms,  */}
      {/* signed amendments, proof of address). Auto-scoped server-side to  */}
      {/* the investor's linked client; visible_to_client forced true.       */}
      {user?.role === 'client' && (
        <ClientUploadRow onUploaded={() => setReloadKey(k => k + 1)} />
      )}

      {grouped.length === 0 && (
        <p className="serif-body" style={{ color: 'var(--ink-2)' }}>
          No documents on file yet. Statements and tax forms appear here when issued; you can upload signed agreements and proof-of-address above.{user?.role === 'client' && <> If you expected to see something here, <a href="/portal/contact" style={{ color: 'var(--ink)' }}>contact us</a>.</>}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
        {grouped.map(([section, items]) => (
          <section key={section}>
            <KickerRow kicker={section.toUpperCase()} />
            <div style={{ borderTop: '1px solid var(--ink)' }}>
              {items.map((d, i) => (
                <div
                  key={d.id}
                  style={{
                    padding: '20px 0',
                    borderBottom: i === items.length - 1 ? '1px solid var(--ink)' : 'var(--hairline)',
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 32, alignItems: 'baseline',
                  }}
                >
                  <span className="serif" style={{ fontSize: 18 }}>{d.title}</span>
                  <Pill>{d.kind.toUpperCase()}</Pill>
                  <span className="label-s">UPLOADED {fmtDate(d.uploaded_at)}</span>
                  {d.has_file ? (
                    <a className="btn-link" href={docUrl(d.id)} download style={{ textDecoration: 'none' }}>
                      Download ↓
                    </a>
                  ) : (
                    <span className="label-s" style={{ color: 'var(--ink-3)' }}>No file</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m && m[2] ? decodeURIComponent(m[2]) : null;
}

function UploadRow({ clientId, onUploaded }: { clientId: string; onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState('agreement');
  const [title, setTitle] = useState('');
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setMsg(null);
    const fd = new FormData();
    fd.append('client_id', clientId);
    fd.append('kind', kind);
    fd.append('title', title || file.name);
    fd.append('visible_to_client', visible ? 'true' : 'false');
    fd.append('file', file);
    try {
      const csrf = getCookie('csrftoken');
      const res = await fetch('/api/v1/documents/', {
        method: 'POST', credentials: 'include',
        headers: csrf ? { 'X-CSRFToken': csrf } : {},
        body: fd,
      });
      if (!res.ok) throw new Error('upload failed');
      setFile(null); setTitle(''); setMsg('Uploaded.'); onUploaded();
    } catch { setMsg('Upload failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ border: 'var(--hairline)', borderColor: 'var(--rule)', padding: 18, marginBottom: 40,
                  display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <span className="label" style={{ marginRight: 8 }}>UPLOAD</span>
      <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} />
      <select className="field-input" value={kind} onChange={e => setKind(e.target.value)} style={{ maxWidth: 180 }}>
        <option value="agreement">Agreement</option>
        <option value="kyc">KYC</option>
        <option value="tax">Tax</option>
        <option value="correspondence">Correspondence</option>
        <option value="statement">Statement</option>
      </select>
      <input className="field-input" placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)} style={{ maxWidth: 220 }} />
      <label className="label-s" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} /> CLIENT-VISIBLE
      </label>
      <button className="btn" disabled={busy || !file} onClick={upload}>{busy ? 'Uploading…' : 'Upload'}</button>
      {msg && <span className="label-s" style={{ color: 'var(--accent)' }}>{msg}</span>}
    </div>
  );
}

// Phase 9.b — investor self-service upload. Backend auto-scopes the
// upload to the linked client; investor can never target another
// client. visible_to_client forced true (investors see their own
// uploads). 25 MB cap + PDF/image MIME whitelist enforced server-side.
function ClientUploadRow({ onUploaded }: { onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [kind, setKind] = useState('kyc');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const upload = async () => {
    if (!file) return;
    setBusy(true); setMsg(null);
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('title', title || file.name);
    fd.append('file', file);
    try {
      const csrf = getCookie('csrftoken');
      const res = await fetch('/api/v1/documents/', {
        method: 'POST', credentials: 'include',
        headers: csrf ? { 'X-CSRFToken': csrf } : {},
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || 'Upload failed.');
      }
      setFile(null); setTitle(''); setMsg('Uploaded.'); onUploaded();
    } catch (e: any) { setMsg(e?.message || 'Upload failed.'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ border: 'var(--hairline)', borderColor: 'var(--rule)', padding: 18, marginBottom: 40,
                  display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <span className="label" style={{ marginRight: 8 }}>UPLOAD A DOCUMENT</span>
      <input type="file" onChange={e => setFile(e.target.files?.[0] ?? null)} />
      <select className="field-input" value={kind} onChange={e => setKind(e.target.value)} style={{ maxWidth: 180 }}>
        <option value="kyc">KYC / proof of address</option>
        <option value="agreement">Signed agreement</option>
        <option value="correspondence">Correspondence</option>
      </select>
      <input className="field-input" placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)} style={{ maxWidth: 220 }} />
      <button className="btn" disabled={busy || !file} onClick={upload}>{busy ? 'Uploading…' : 'Upload'}</button>
      {msg && <span className="label-s" style={{ color: 'var(--accent)' }}>{msg}</span>}
    </div>
  );
}
